import dayjs from 'dayjs';
import { aiFromText } from './dealer';
import { formatMonth } from '../view/format';

// The inspection's odometer reading, as one more point on the dealer mileage chart
const inspectionPoint = vehicle => {
    if (!vehicle || !(Number(vehicle.mileage_km) > 0) || !vehicle.updated_at) return null;
    const d = dayjs(vehicle.updated_at);
    return {
        y: d.year(),
        m: d.month() + 1,
        d: d.date(),
        date: formatMonth(d),
        km: Number(vehicle.mileage_km),
        inspection: true,
    };
};

// SC-QA-16: the inspection is already on the page, so say at the top that the dealer part is still coming
const dealerProcessingNotice = t => ({
    tone: 'neutral',
    title: t('adapter.notices.dealerProcessingTitle'),
    text: t('adapter.notices.dealerProcessingText'),
    screenOnlyText: true,
});

// dealer fields the inspection already shows inside another field: fuel in Engine, doors in Body type
const COVERED_BY_INSPECTION = { fuel: 'engineSize', doors: 'bodyType' };

// the inspection's fields, then the dealer's that add something: not the same key, label or covered value
const mergeFields = (internalFields, dealerFields) => {
    const keys = new Set(internalFields.map(f => f.key));
    const labels = new Set(internalFields.map(f => f.label));
    return [
        ...internalFields,
        ...dealerFields.filter(
            f => !keys.has(f.key) && !labels.has(f.label) && !keys.has(COVERED_BY_INSPECTION[f.key])
        ),
    ];
};

// one card per status: a dealer due date replaces the inspection's card for the same thing
const mergeStatus = (internalCards, dealerCards) => {
    const dealerKeys = new Set(dealerCards.map(card => card.key).filter(Boolean));
    return [...internalCards.filter(card => !card.key || !dealerKeys.has(card.key)), ...dealerCards];
};

// the dealer report's overview (combined with our data) once it is read; until then, or when the dealer refused the
// order, the overview built from our internal data alone. When that one could not be made either, the section says so
// instead of disappearing (SC-QA-74)
const aiOfComplete = (dealer, internalOverview, overviewMissing) => {
    if (dealer.ai && dealer.ai.status === 'ok') return dealer.ai;
    if (internalOverview) return aiFromText(internalOverview, 'inspection');
    // a read dealer report keeps its own state (its "everything below is complete" note holds); the inspection-only
    // notice is for a service history that is still being checked or was refused
    return overviewMissing && !dealer.ai ? { status: 'unavailable', basis: 'inspection' } : dealer.ai;
};

/**
 * The Complete report: our inspection (internal view model) and the dealer part (dealer view model, parsed or
 * a state such as waiting) on one page. The inspection gives the vehicle; the dealer adds what it alone holds.
 * `internalOverview` is the AI overview of our internal data, shown until the dealer report has its own;
 * `overviewMissing` says it was asked for and none came back (Gemini failed, or nothing is stored while impersonating).
 */
export const combineReports = (
    internal,
    dealer,
    internalVehicle,
    t,
    internalOverview = null,
    overviewMissing = false
) => {
    if (!internal) return dealer;
    if (!dealer) return internal;

    // the dealer part has visits or repair rows to count. A parsed document with no records is not enough: its
    // stats are all empty, so the inspection keeps its tiles and the page shows the "no service records" panel
    const hasRecords = !!(dealer.timeline || dealer.repairs);
    const points = [...((dealer.mileage && dealer.mileage.points) || []), inspectionPoint(internalVehicle)]
        .filter(Boolean)
        .sort((a, b) => a.y * 400 + a.m * 31 + (a.d || 15) - (b.y * 400 + b.m * 31 + (b.d || 15)));

    return {
        reportDate: internal.reportDate || dealer.reportDate,
        source: t('adapter.sourceCombined'),
        vehicle: {
            ...internal.vehicle,
            fields: mergeFields(internal.vehicle.fields, (dealer.vehicle && dealer.vehicle.fields) || []),
        },
        // the dealer's visit numbers once its report has records; until then the inspection's
        tiles: hasRecords ? undefined : internal.tiles,
        stats: hasRecords ? dealer.stats : undefined,
        pricing: internal.pricing,
        condition: internal.condition,
        status: mergeStatus(internal.status || [], dealer.status || []),
        notices: [...(dealer.waiting ? [dealerProcessingNotice(t)] : []), ...(dealer.notices || [])],
        waiting: dealer.waiting,
        outcome: dealer.outcome,
        original: dealer.original,
        inspection: internal.inspection,
        ai: aiOfComplete(dealer, internalOverview, overviewMissing),
        history: dealer.history,
        timeline: dealer.timeline,
        repairs: dealer.repairs,
        mileage: { points },
        equipment: dealer.equipment || internal.equipment,
        docinfo: dealer.docinfo,
        empty: dealer.empty,
    };
};
